import { Column } from '@tanstack/react-table';
import classNames from 'classnames';
import CalendarIcon from '@images/svg/date.svg?react';
import CrossIcon from '@images/svg/close.svg?react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { getCurrentLocale } from '@utils/locales';
import { dateToYearMonthDayString } from '@utils/dateUtils';

export type DateRangeFilterValue = [number | undefined, number | undefined] | undefined;

interface Props<TData> {
    column: Column<TData>;
}

const DateRangeFilter = <TData extends object>({ column }: Props<TData>) => {
    const columnFilterValue = column.getFilterValue() as DateRangeFilterValue;
    const [currentStartTimeStamp, currentEndTimeStamp] = columnFilterValue ?? [];
    const currentStartDate = currentStartTimeStamp ? new Date(currentStartTimeStamp) : null;
    const currentEndDate = currentEndTimeStamp ? new Date(currentEndTimeStamp) : null;
    const { t } = useTranslation();
    const currentLocale = getCurrentLocale();
    registerLocale(currentLocale.code, currentLocale.dateLocale);

    const onReset = () => {
        column.setFilterValue(undefined);
    };

    const onFilterChangeV2 = (newRange: [Date | null, Date | null]) => {
        const [start, end] = newRange;
        column.setFilterValue([new Date(dateToYearMonthDayString(start)).getTime(), new Date(dateToYearMonthDayString(end)).getTime()]);
    };

    return (
        <div
            className={classNames('gridFilter', 'gridFilter--date', {
                'gridFilter--active': columnFilterValue,
            })}
        >
            <DatePicker
                className="form__date"
                portalId="theme"
                disabledKeyboardNavigation
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                locale={currentLocale.code}
                dateFormat={currentLocale.dateFormat}
                selectsRange
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date(2100, 0, 1)}
                onChange={onFilterChangeV2}
                startDate={currentStartDate || undefined}
                endDate={currentEndDate || undefined}
                todayButton={t('today')}
                autoComplete="off"
                onKeyDown={(e) => {
                    e.preventDefault();
                }}
            />
            {!columnFilterValue ? <CalendarIcon className="defaultGridFilter__icon" /> : null}
            {columnFilterValue ? (
                <button type="button" tabIndex={-1} className="defaultGridFilter__reset" onClick={onReset}>
                    <CrossIcon />
                </button>
            ) : null}
        </div>
    );
};

export default DateRangeFilter;
